import { useState } from "react"
import { Box, IconButton, TextField } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPaperPlaneTop } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"

const MAX_CHARS = 1000

const useStyles = makeStyles()((theme) => ({
    container: {
        paddingTop: "1rem",
        display: "flex",
        gap: "0.625rem",
        alignItems: "flex-start",
    },
    inputWrapper: {
        flex: 1,
    },
    input: {
        "& .MuiOutlinedInput-root": {
            borderRadius: "0.75rem",
            minHeight: "2.75rem",
            backgroundColor: theme.palette.common.white,
        },
    },
    sendButton: {
        border: `1px solid ${tailwindPalette.slate[300]}`,
        borderRadius: "0.75rem",
        padding: "0.5rem",
        width: "2.75rem",
        height: "2.75rem",
        fontSize: "1.25rem",
        "&:hover": {
            backgroundColor: tailwindPalette.slate[100],
        },
    },
}))

type OrderChatInputProps = Readonly<{
    onSend: (content: string) => void
    disabled?: boolean
    placeholder: string
}>

export function OrderChatInput({ onSend, disabled, placeholder }: OrderChatInputProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [value, setValue] = useState("")

    const trimmedValue = value.trim()
    const exceeded = value.length > MAX_CHARS
    const canSend = trimmedValue.length > 0 && !exceeded && !disabled

    const handleSend = () => {
        if (!canSend) return
        onSend(trimmedValue)
        setValue("")
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.inputWrapper}>
                <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    size="small"
                    disabled={disabled}
                    className={classes.input}
                    error={exceeded}
                    helperText={exceeded ? l10n.getString("order-chat-exceeded-characters", { limit: MAX_CHARS }) : undefined}
                />
            </Box>
            <IconButton onClick={handleSend} disabled={!canSend} className={classes.sendButton}>
                <FontAwesomeIcon
                    icon={faPaperPlaneTop}
                    color={canSend ? tailwindPalette.slate[900] : tailwindPalette.slate[300]}
                />
            </IconButton>
        </Box>
    )
}
